/**
 * Shared Axios client factory with standard auth headers and error handling.
 * Replaces the baseApi.js pattern from panels-operations.
 */
import axios, { type AxiosInstance } from 'axios'
import { getSessionToken, getLoginUserEmail, handleUnauthorized } from '@/lib/auth'
import { showToastMessage } from '@/utils'
import { reportApiUnavailable } from '@/lib/maintenance'

export function createAxiosClient(baseURL: string): AxiosInstance {
  const api = axios.create({ baseURL })

  // Request: attach auth headers
  api.interceptors.request.use((config) => {
    const token = getSessionToken()
    if (token) {
      config.headers['x-auth-key'] = token
      config.headers['Authorization'] = `Bearer ${token}`
    }
    config.headers['user-email'] = getLoginUserEmail()

    // Let browser set multipart boundary for FormData
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    } else if (typeof config.data === 'object' && config.data !== null) {
      config.headers['Content-Type'] = 'application/json'
    }

    return config
  })

  // Response: handle 401 + reject with response data
  api.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response) {
        if (error.response.status === 401 && !window.location.pathname.includes('login')) {
          handleUnauthorized()
        }
        const responseData = error.response.data
        // Temporary backend failure to verify the account: keep the session, ask to retry.
        if (error.response.status === 503 && responseData?.error === 'could not verify account') {
          showToastMessage('Could not verify your account right now. Please retry in a moment.', false)
        } else if ([502, 503, 504].includes(error.response.status)) {
          // Backend is restarting or deploying (gateway answers 502/503/504): swap the app for the maintenance page.
          reportApiUnavailable()
        }
        if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
          return Promise.reject({ ...responseData, status: error.response.status })
        }
        return Promise.reject({
          message: typeof responseData === 'string' ? responseData : error.message,
          status: error.response.status,
        })
      }
      // Request sent but nothing came back while the browser is online: backend is down or restarting.
      if (error.request && navigator.onLine) reportApiUnavailable()
      return Promise.reject(error.message)
    },
  )

  return api
}
